import React, { useCallback, useEffect, useRef } from 'react';
import { Provider , useDispatch , useSelector} from 'react-redux';
import useJourneyInactivity, {
  useSessionTimeoutScopeActive,
} from './hook/inactivityTimerHook';
import { Modal } from './common/components';
import { MemoryRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { PersistGate } from "redux-persist/integration/react";
import store, { persistor } from "./store";
import {
  CommonJourneyLead,
  CommonJourneySummary,
  CommonJourneyCustomerVerification,
  CommonJourneyProposal,
  PaymentReturn,
  ProposalReview,
  TrackForm,
  TransferOfFunds,
} from './insurance-journey';
import NischitPensionQuote from './insurance-journey/quote/NischitPensionQuote';
import ErrorBoundary from './common/components/ErrorBoundary';
import {
  clearJourneySessionState,
  getRestartPath,
  getRestartPageUrl,
  JOURNEY_START_PATH_KEY,
  JOURNEY_INITIATED_PAGE_URL_KEY,
  setStoredApplicationNumber,
  setStoredCaseId,
} from './common/utils';
import { REINITIATE_PAYMENT_SUMMARY_SESSION_KEY , JOURNEY_TYPES } from './constants';
import { journeyActions } from './store/journeySlice';
import { getJourneyData } from './api/journeyService';
import {
  hydrateStoreFromJourney,
  getResumeRouteFromJourney,
  getResumeStateFromJourney,
} from './common/utils/journeyResume';
import { logError } from './common/utils/logger';
import { setCurrentJourneyContext } from './common/utils/journeyContextRuntime';
import { decryptJourneyQueryValue, encryptJourneyQueryValue } from './common/utils/journeyEncryption';
import { getResumeDobStorageKey } from './common/utils/journeyOwnershipDob';
import { scrollToTopOnMount } from './common/utils/scrollToTop';

const STAGE_TO_PATH = {
  lead: '/',
  quote: '/quote',
  summary: '/summary',
  customerVerification: '/verification',
  proposal: '/proposal',
};
const NISCHIT_PENSION_COMPONENTS = {
  Lead: CommonJourneyLead,
  Quote: NischitPensionQuote,
  Summary: CommonJourneySummary,
  CustomerVerification: CommonJourneyCustomerVerification,
  Proposal: CommonJourneyProposal,
};

const resolveJourneyComponents = (journeyType) => {
  switch (journeyType) {
    case JOURNEY_TYPES.NISCHIT_PENSION:
    default:
      return NISCHIT_PENSION_COMPONENTS;
  }
};

function resolveInitialPath(initialPathProp, leadCompleted, initialStage) {
  if (initialPathProp != null) {
    return initialPathProp;
  }
  if (leadCompleted === 'true') {
    return '/quote';
  }
  return STAGE_TO_PATH[initialStage] || '/';
}

function parseApplicationNumberFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const applicationNumberCipher = params.get('applicationNumber')?.trim() || null;
  const applicationNumberPlain = applicationNumberCipher
    ? (decryptJourneyQueryValue(applicationNumberCipher) || applicationNumberCipher)
    : null;
  return { params, applicationNumberCipher, applicationNumberPlain };
}

function getDateOfBirthFromLeadSession() {
  try {
    const rawLeadData = sessionStorage.getItem('inlic_lead_form_data');
    if (!rawLeadData) return null;
    const parsedLeadData = JSON.parse(rawLeadData);
    return parsedLeadData?.dateOfBirth?.trim?.() || null;
  } catch {
    return null;
  }
}

function readScopedResumeDob(applicationNumberPlain) {
  if (!applicationNumberPlain) return null;
  try {
    return sessionStorage.getItem(getResumeDobStorageKey(applicationNumberPlain));
  } catch {
    return null;
  }
}

function resolveResumeDob(applicationNumberPlain, leadFormData, dateOfBirthFromUrl) {
  const dateOfBirthFromState = leadFormData?.dateOfBirth?.trim?.() || null;
  const dateOfBirthFromSession = getDateOfBirthFromLeadSession();
  const scopedResumeDob = readScopedResumeDob(applicationNumberPlain);
  return (
    dateOfBirthFromState ||
    dateOfBirthFromSession ||
    scopedResumeDob ||
    dateOfBirthFromUrl ||
    null
  );
}

function isReinitiateSummarySession() {
  try {
    return (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(REINITIATE_PAYMENT_SUMMARY_SESSION_KEY) === '1'
    );
  } catch {
    return false;
  }
}

function getGlobalResumeSkipReason({
  applicationNumberPlain,
  pathname,
  resumeDob,
  hydratedAppNumber,
  isHydrating,
}) {
  if (!applicationNumberPlain) return 'no-app-number';
  if (pathname === '/payment-return') return 'payment-return';
  if (hydratedAppNumber === applicationNumberPlain || isHydrating) return 'already-hydrated';
  if (!resumeDob) return 'no-dob';
  return null;
}

function buildPathWithQuery(pathname, searchString) {
  if (!searchString) return pathname;
  return `${pathname}?${searchString}`;
}

function updateEncryptedApplicationNumberInUrl({
  journeyAppNumber,
  applicationNumberPlain,
  applicationNumberCipher,
  params,
  pathname,
  navigate,
}) {
  try {
    const encrypted = encryptJourneyQueryValue(journeyAppNumber || applicationNumberPlain);
    if (!encrypted || !applicationNumberCipher || encrypted === applicationNumberCipher) return;
    params.set('applicationNumber', encrypted);
    navigate(buildPathWithQuery(pathname, params.toString()), { replace: true });
  } catch {
    // ignore
  }
}

function navigateToResumeRouteIfNeeded({
  journey,
  pathname,
  fromPaymentReturn,
  reinitiateSummarySession,
  navigate,
}) {
  const resumeRoute = getResumeRouteFromJourney(journey);
  const resumeState = resumeRoute === '/proposal' ? getResumeStateFromJourney(journey) : null;
  const stayOnSummaryAfterFailedPayment =
    pathname === '/summary' && (fromPaymentReturn === '1' || reinitiateSummarySession);
  if (resumeRoute && pathname !== resumeRoute && !stayOnSummaryAfterFailedPayment) {
    navigate(resumeRoute, resumeState ? { state: resumeState } : {});
  }
}

function applyJourneyHydration({
  journey,
  dispatch,
  applicationNumberPlain,
  applicationNumberCipher,
  params,
  pathname,
  fromPaymentReturn,
  reinitiateSummarySession,
  navigate,
}) {
  hydrateStoreFromJourney(
    dispatch,
    journey,
    applicationNumberPlain,
    store.getState,
  );

  const journeyAppNumber =
    journey.applicationNumber ||
    journey.application_number ||
    applicationNumberPlain;
  const journeyCaseId =
    journey.caseId ||
    journey.c_caseId ||
    journey.case_number ||
    null;
  if (journeyAppNumber) setStoredApplicationNumber(journeyAppNumber);
  if (journeyCaseId) setStoredCaseId(journeyCaseId);

  updateEncryptedApplicationNumberInUrl({
    journeyAppNumber,
    applicationNumberPlain,
    applicationNumberCipher,
    params,
    pathname,
    navigate,
  });

  navigateToResumeRouteIfNeeded({
    journey,
    pathname,
    fromPaymentReturn,
    reinitiateSummarySession,
    navigate,
  });
}

function JourneyRoutes({ journeyType, journeyRoutePath }) {
  const state = useSelector((s) => s.journey);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    Lead,
    Quote,
    Summary,
    CustomerVerification,
    Proposal,
  } = resolveJourneyComponents(journeyType) ?? {};
  const hydratedAppRef = useRef(null);
  const isHydratingRef = useRef(false);

  
  // Remember where the user started the journey: in-app path and full page URL (for inactivity restart).
  // Only set when on / or /home so we don't overwrite after redirect to /common-journey?applicationNumber=...
  // For /common-journey, set in-app path only if not already set (user opened common-journey page directly).
  useEffect(() => {
    try {
      if (location.pathname === '/' || location.pathname === '/home') {
        sessionStorage.setItem(JOURNEY_START_PATH_KEY, location.pathname);
        const pageUrl = typeof window !== 'undefined' && window.location
          ? `${window.location.origin}${window.location.pathname}`
          : '';
        if (pageUrl) sessionStorage.setItem(JOURNEY_INITIATED_PAGE_URL_KEY, pageUrl);
      } else if (location.pathname === journeyRoutePath && !sessionStorage.getItem(JOURNEY_START_PATH_KEY)) {
        sessionStorage.setItem(JOURNEY_START_PATH_KEY, journeyRoutePath);
      }
    } catch {
      /* ignore */
    }
  }, [location.pathname, journeyRoutePath]);

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') return;
    if (location.pathname === '/summary') return;
    try {
      sessionStorage.removeItem(REINITIATE_PAYMENT_SUMMARY_SESSION_KEY);
    } catch {
      /* ignore */
    }
  }, [location.pathname]);

    // Always land at top when navigating between journey screens (lead ↔ quote ↔ summary ↔ …).
  useEffect(() => {
    scrollToTopOnMount();
  }, [location.pathname]);

  // Global journey resume:
  // Any route opened with ?applicationNumber=... should rehydrate store from backend
  // and land on the exact stage where user left off.
  useEffect(() => {
    const { params, applicationNumberCipher, applicationNumberPlain } =
      parseApplicationNumberFromSearch(location.search);
    const dateOfBirthFromUrl = params.get('dateOfBirth')?.trim() || null;
    const resumeDob = resolveResumeDob(
      applicationNumberPlain,
      state?.leadFormData,
      dateOfBirthFromUrl,
    );
    const fromPaymentReturn = params.get('fromPaymentReturn');
    const reinitiateSummarySession = isReinitiateSummarySession();
    const skipReason = getGlobalResumeSkipReason({
      applicationNumberPlain,
      pathname: location.pathname,
      resumeDob,
      hydratedAppNumber: hydratedAppRef.current,
      isHydrating: isHydratingRef.current,
    });

    if (skipReason === 'no-dob') {
      window.location.href = '/resume-journey';
      return;
    }
    if (skipReason) return;

    let cancelled = false;
    isHydratingRef.current = true;

    getJourneyData(applicationNumberPlain, resumeDob)
      .then((journey) => {
        if (cancelled || !journey || typeof journey !== 'object') return;

        applyJourneyHydration({
          journey,
          dispatch,
          applicationNumberPlain,
          applicationNumberCipher,
          params,
          pathname: location.pathname,
          fromPaymentReturn,
          reinitiateSummarySession,
          navigate,
        });
        hydratedAppRef.current = applicationNumberPlain;
      })
      .catch((err) => {
        logError('[JourneyRoutes] Global resume hydration failed', err);
        navigate('/');
      })
      .finally(() => {
        isHydratingRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate, location.pathname, location.search]);

  const handleRestart = useCallback(() => {
    const restartPageUrl = getRestartPageUrl();
    const restartPath = getRestartPath();
    clearJourneySessionState({ clearLocalStorage: true, purgeReduxPersist: true });
    dispatch(journeyActions.resetJourney());
    if (restartPageUrl) {
      window.location.href = restartPageUrl;
    } else {
      navigate(restartPath);
    }
  }, [dispatch, navigate]);

  /** Idle session modal only on common-journey / payment-success / payment-failure pages, not site-wide. */
  const sessionTimeoutScopeActive = useSessionTimeoutScopeActive();
  const { showModal, secondsLeft, closeModal, restartJourney } = useJourneyInactivity(
    handleRestart,
    sessionTimeoutScopeActive,
  );

  return (
    <>
      <Routes>
        <Route path="/" element={<Lead mode="basic" journeyType={journeyType} />} />
        <Route path="/home" element={<Lead mode="basic" journeyType={journeyType} />} />
        <Route path={journeyRoutePath} element={<Lead mode="personalize" journeyType={journeyType} />} />
        <Route path="/payment-return" element={<PaymentReturn />} />
        <Route path="/quote" element={<Quote />} />
        <Route path="/summary" element={<Summary />} />
        <Route path="/transfer-of-funds" element={<TransferOfFunds />} />
        <Route path="/verification" element={<CustomerVerification />} />
        <Route path="/proposal" element={<Proposal />} />
        <Route path="/proposal-review" element={<ProposalReview />} />
        <Route path="/track-your-application" element={<TrackForm />} />
      </Routes>

      <Modal
        isOpen={sessionTimeoutScopeActive && showModal}
        onClose={secondsLeft === 0 ? restartJourney : closeModal}
        title="Session Timeout"
        modalNode="session"
      >
        <p>
          You have been inactive. Your session will expire in{" "}
          <strong>{secondsLeft}</strong> seconds.
        </p>

        <div className="modal-footer">
          <button
            className="btn secondary-button regular"
            disabled={secondsLeft > 0}
            onClick={restartJourney}
          >
            Continue
          </button>
          <button
            className="btn primary-button regular"
            onClick={secondsLeft === 0 ? restartJourney : closeModal}
          >
            Close
          </button>
        </div>
      </Modal>
    </>
  );
}

function App({
  initialStage = "lead",
  initialPath: initialPathProp,
  resetJourneyOnMount = false,
  journeyConfig = {},
}) {
  const leadCompleted = sessionStorage.getItem("leadCompleted");
  const journeyType = journeyConfig?.journeyType || JOURNEY_TYPES.NISCHIT_PENSION;
  const journeyRoutePath = journeyConfig?.routePath;

  const initialPath = resolveInitialPath(initialPathProp, leadCompleted, initialStage);

  // After refresh we clear session in main.jsx but the store was already created with old localStorage.
  useEffect(() => {
    if (resetJourneyOnMount) {
      store.dispatch(journeyActions.resetJourney());
    }
  }, [resetJourneyOnMount]);

  useEffect(() => {
    setCurrentJourneyContext({
      journeyType,
      formNames: journeyConfig?.formNames || {},
      routePath: journeyRoutePath,
    });
  }, [journeyType, journeyConfig?.formNames, journeyRoutePath]);

  return (
    <Provider store={store}>
       <PersistGate loading={null} persistor={persistor}>
        <MemoryRouter initialEntries={[initialPath]}>
          <div className="online-sales-journey-container">
            <ErrorBoundary>
              <JourneyRoutes journeyType={journeyType} journeyRoutePath={journeyRoutePath} />
            </ErrorBoundary>
          </div>
        </MemoryRouter>
       </PersistGate>
    </Provider>
  );
}

export default App;
